import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DetalleCampana from './DetalleCampana';
import NuevaCampanaForm from './NuevaCampanaForm';
import Progreso from './Progreso';
import { listarCampanas } from './campanasApi';
import { F, L } from './estilos';
import { ESTADOS_CAMPANA, esPlantillaApta, fechaLocal, hayEnviando, mensajeDeProblema } from '../../utils/campanas';

const REFRESCO_MS = 10000;

const FilaCampana = ({ campana, onAbrir }) => (
  <tr style={L.fila} onClick={onAbrir}>
    <td style={L.td} data-label="Nombre"><span style={L.nombre}>{campana.nombre}</span></td>
    <td style={L.td} data-label="Plantilla"><span className="campanas-plantilla" style={L.plantilla}>{`${campana.templateName} (${campana.languageCode})`}</span></td>
    <td style={L.tdSuave} data-label="Envío">{`${fechaLocal(campana.programadaParaLocal)} (${campana.zonaHoraria})`}</td>
    <td style={L.td} data-label="Estado"><span style={L.estado(campana.estado)}>{ESTADOS_CAMPANA[campana.estado]}</span></td>
    <td style={L.td} data-label="Progreso"><Progreso campana={campana} /></td>
  </tr>
);

/** Tercera pestaña de plantillas (§10): listado, nueva campaña y detalle. */
const CampanasTab = ({ botId, zonaHoraria, templates, onSuccess, onError }) => {
  const [campanas, setCampanas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [vista, setVista] = useState({ tipo: 'lista' });
  const [botActivo, setBotActivo] = useState(botId);
  const [pagina, setPagina] = useState(0);
  const [paginacion, setPaginacion] = useState({ hasNext: false, hasPrevious: false });
  // Los avisos llegan como flechas en línea: en un ref no reinician los efectos.
  const avisos = useRef({ onSuccess, onError });
  avisos.current = { onSuccess, onError };
  const solicitud = useRef(0);
  const aptas = useMemo(() => templates.filter(esPlantillaApta), [templates]);

  if (botId !== botActivo) {
    setBotActivo(botId);
    setCampanas([]);
    setVista({ tipo: 'lista' });
    setPagina(0);
    setPaginacion({ hasNext: false, hasPrevious: false });
    solicitud.current += 1;
  }

  const cargar = useCallback(async () => {
    if (!botId) return;
    const marca = ++solicitud.current;
    setCargando(true);
    const r = await listarCampanas(botId, pagina);
    if (marca !== solicitud.current) return;
    setCargando(false);
    if (r.ok) {
      setCampanas(r.data.content || []);
      setPaginacion({ hasNext: Boolean(r.data.hasNext), hasPrevious: Boolean(r.data.hasPrevious) });
    } else avisos.current.onError(mensajeDeProblema(r.data, 'No se pudieron cargar las campañas.'));
  }, [botId, pagina]);

  useEffect(() => { cargar(); }, [cargar]);

  const enviando = hayEnviando(campanas);
  useEffect(() => {
    if (!enviando) return undefined;
    const temporizador = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(temporizador);
  }, [enviando, cargar]);

  const volverAlListado = () => {
    setVista({ tipo: 'lista' });
    cargar();
  };

  if (vista.tipo === 'nueva') {
    return (
      <NuevaCampanaForm
        key={botId}
        botId={botId}
        zonaHoraria={zonaHoraria}
        plantillas={aptas}
        onCreada={() => { avisos.current.onSuccess('Campaña creada'); volverAlListado(); }}
        onError={(m) => avisos.current.onError(m)}
        onCancelar={() => setVista({ tipo: 'lista' })}
      />
    );
  }
  if (vista.tipo === 'detalle') {
    return (
      <DetalleCampana
        campanaId={vista.id}
        onVolver={volverAlListado}
        onSuccess={(m) => avisos.current.onSuccess(m)}
        onError={(m) => avisos.current.onError(m)}
      />
    );
  }
  return (
    <div className="campanas">
    <div className="campanas-tarjeta" style={L.tarjeta}>
      <div className="campanas-cabecera" style={L.cabecera}>
        <div>
          <h3 style={L.titulo}>Campañas</h3>
          <p className="campanas-texto" style={L.subtitulo}>Envíos masivos de una plantilla aprobada. Pulsa una campaña para ver sus destinatarios.</p>
        </div>
        <div className="campanas-acciones" style={L.acciones}>
          <button type="button" style={F.btnGhost} onClick={cargar} disabled={cargando}>
            {cargando ? 'Cargando…' : 'Actualizar'}
          </button>
          <button type="button" style={F.btnPrimary} onClick={() => setVista({ tipo: 'nueva' })}>
            + Nueva campaña
          </button>
        </div>
      </div>
      {campanas.length === 0 && !paginacion.hasNext && pagina === 0 ? (
        <div style={L.vacio}>Todavía no hay campañas.</div>
      ) : campanas.length > 0 && (
        <table className="campanas-tabla" style={L.tabla}>
          <thead>
            <tr>
              <th style={L.th}>Nombre</th>
              <th style={L.th}>Plantilla</th>
              <th style={L.th}>Envío</th>
              <th style={L.th}>Estado</th>
              <th style={L.th}>Progreso</th>
            </tr>
          </thead>
          <tbody>
            {campanas.map((c) => (
              <FilaCampana key={c.id} campana={c} onAbrir={() => setVista({ tipo: 'detalle', id: c.id })} />
            ))}
          </tbody>
        </table>
      )}
      {(paginacion.hasNext || paginacion.hasPrevious) && (
        <div className="campanas-paginacion" style={L.paginacion}>
          <button type="button" style={F.btnGhost} disabled={!paginacion.hasPrevious} onClick={() => setPagina((p) => p - 1)}>
            Anterior
          </button>
          <button type="button" style={F.btnGhost} disabled={!paginacion.hasNext} onClick={() => setPagina((p) => p + 1)}>
            Siguiente
          </button>
        </div>
      )}
    </div>
    </div>
  );
};

export default CampanasTab;
